/**
 * creditCounter — pure tween math for the self-contained animated integer counter.
 *
 * No external animation library (by design). The AnimatedCounter component drives
 * requestAnimationFrame and asks tweenValue() for the integer to show at each
 * frame; reduced-motion users snap straight to the target. Pure so it unit-tests
 * directly (mirrored in cypress/node-tests/wallet-counter.test.js — KEEP IN SYNC).
 */

// Default tween length — a quick ~half-second settle.
export const DEFAULT_TWEEN_MS = 500;

/** ease-out cubic: fast start, gentle settle. Clamped to [0,1]. */
export function easeOutCubic(t) {
  const c = Math.min(1, Math.max(0, Number(t) || 0));
  return 1 - Math.pow(1 - c, 3);
}

/**
 * The integer to display `elapsed` ms into a tween from `from` to `to` over
 * `duration` ms. Reaching/exceeding `duration` (or a non-positive `duration`)
 * returns `to` EXACTLY, so the animation always lands on the true target with no
 * rounding drift; `elapsed <= 0` returns `from`.
 */
export function tweenValue(from, to, elapsed, duration) {
  const start = Number(from) || 0;
  const end = Number(to) || 0;
  const dur = Number(duration) || 0;
  const t = Number(elapsed) || 0;
  if (dur <= 0 || t >= dur) return end;
  if (t <= 0) return start;
  const eased = easeOutCubic(t / dur);
  return Math.round(start + (end - start) * eased);
}

/** Respect the OS "reduce motion" setting — snap instead of animate. */
export function prefersReducedMotion() {
  try {
    return !!(typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (_) {
    return false;
  }
}
